import { getDefaultConfig } from '@rainbow-me/rainbowkit';
import { polygon, polygonAmoy } from 'wagmi/chains';
import { http, fallback } from 'wagmi';
import logger from './utils/logger';

const isMetaMaskInAppBrowser = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  const ua = window.navigator.userAgent || '';
  if (/MetaMaskMobile/i.test(ua)) {
    return true;
  }
  return Boolean(window.ethereum?.isMetaMask) && /Mobile|Android|iPhone|iPad/i.test(ua);
};

// Optimized Polygon RPC endpoints with prioritization
// Primary: User's custom RPC (fastest, most reliable)
// Secondary: Public RPCs that tolerate browser JSON-RPC (CORS + no API key)
//
// Do NOT use https://polygon-rpc.com from the browser — it commonly returns 401 Unauthorized
// for unauthenticated POSTs (that endpoint is aimed at server / keyed traffic).
const POLYGON_RPC_COM = /polygon-rpc\.com/i;
const rawEnvRpc = (process.env.REACT_APP_POLYGON_RPC || '').trim();
const envRpcIsBlocked = rawEnvRpc && POLYGON_RPC_COM.test(rawEnvRpc);
if (envRpcIsBlocked) {
  logger.warn(
    '[rpc] REACT_APP_POLYGON_RPC uses polygon-rpc.com, which often returns 401 in the browser. ' +
      'Remove it or set REACT_APP_POLYGON_RPC to https://polygon.drpc.org or an Alchemy/Infura URL. ' +
      'Using public fallbacks for now.'
  );
}

const userRpc = !envRpcIsBlocked && rawEnvRpc ? [rawEnvRpc] : [];

const polygonRpcEndpoints = [
  ...userRpc,
  'https://polygon.drpc.org',
  'https://1rpc.io/matic',
  'https://polygon-bor-rpc.publicnode.com',
  // Removed: polygon-rpc.com — 401 from browsers; chainstack/onfinality — CORS / rate limits
];

const walletConnectProjectId = (process.env.REACT_APP_WALLETCONNECT_PROJECT_ID || '').trim();
const isProductionBuild = process.env.NODE_ENV === 'production';

if (isProductionBuild && !walletConnectProjectId) {
  throw new Error(
    '[walletconnect] REACT_APP_WALLETCONNECT_PROJECT_ID is required for production builds. ' +
      'Create a project at https://cloud.walletconnect.com and add your domain to allowed origins.'
  );
}

if (!walletConnectProjectId) {
  logger.warn(
    '[walletconnect] REACT_APP_WALLETCONNECT_PROJECT_ID is not set (dev only). ' +
      'WalletConnect mobile sessions may fail until you set it in frontend/.env'
  );
}

/** Dev-only fallback; never used when NODE_ENV=production (build throws above). */
const DEV_WALLETCONNECT_PROJECT_ID = '3733021249148792b765f929867e90b8';

export const config = getDefaultConfig({
  appName: '3Dragons Web3 Sports Trading',
  projectId: walletConnectProjectId || DEV_WALLETCONNECT_PROJECT_ID,
  chains: [polygon, polygonAmoy],
  transports: {
    [polygon.id]: fallback(
      polygonRpcEndpoints.map(url => http(url, { 
        // Timeout after 8 seconds per RPC (reduced from 10s)
        timeout: 8000,
        // Retry failed requests once
        retryCount: 1,
      })),
      {
        // Only retry 2 endpoints before failing (reduced from all endpoints)
        // This prevents excessive retries when endpoints are down
        retryCount: 2,
        // Rank by response time
        rank: true,
      }
    ),
    [polygonAmoy.id]: http(),
  },
  ssr: false,
  
  // 🔒 Privacy: Disable Coinbase analytics and tracking
  appInfo: {
    appName: '3Dragons Web3 Sports Trading',
    appDescription: 'Decentralized sports prediction markets on Polygon',
    appUrl: 'https://3dragons.io',
    disclaimer: null, // Remove Coinbase disclaimer
  },
  
  // Wallet connection settings
  walletConnectOptions: {
    // Show QR code modal for mobile wallets
    showQrModal: !isMetaMaskInAppBrowser(),
    metadata: {
      name: '3Dragons Web3 Sports Trading',
      description: 'Decentralized sports prediction markets on Polygon',
      url: 'https://3dragons.io',
      icons: ['https://3dragons.io/dragon3-icon.png'],
    },
  },
});

